import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, Loader2, RotateCcw, X, Plus, AlertTriangle, Clock, Package, Users } from "lucide-react";

export default function AdminA1Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [newStage, setNewStage] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/a1/settings");
      setSettings(data);
      setForm({
        valid_subscription_stages: data.valid_subscription_stages || [],
        stock_orange_threshold: data.stock_orange_threshold,
        stock_red_threshold: data.stock_red_threshold,
        wait_client_hours: data.wait_client_hours,
        escalate_supervisor_hours: data.escalate_supervisor_hours,
        zone_capacity_override: data.zone_capacity_override,
      });
    } catch (e) {
      toast.error("Error cargando settings: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty =
    settings && form &&
    (JSON.stringify(form.valid_subscription_stages) !== JSON.stringify(settings.valid_subscription_stages) ||
      form.stock_orange_threshold !== settings.stock_orange_threshold ||
      form.stock_red_threshold !== settings.stock_red_threshold ||
      form.wait_client_hours !== settings.wait_client_hours ||
      form.escalate_supervisor_hours !== settings.escalate_supervisor_hours ||
      form.zone_capacity_override !== settings.zone_capacity_override);

  const save = async () => {
    if (!form) return;
    if (form.stock_orange_threshold >= form.stock_red_threshold) {
      toast.error("El umbral naranja debe ser menor que el rojo");
      return;
    }
    if (form.valid_subscription_stages.length === 0) {
      toast.error("Debes tener al menos un estado de suscripción válido");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/a1/settings", form);
      setSettings(data);
      toast.success("Configuración guardada. Los cambios aplican en la próxima evaluación (caché TTL 30s).");
    } catch (e) {
      toast.error("Error guardando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const addStage = () => {
    const s = newStage.trim();
    if (!s) return;
    if (form.valid_subscription_stages.some((x) => x.toLowerCase() === s.toLowerCase())) {
      toast.warning("Ese estado ya está en la lista");
      return;
    }
    setForm({ ...form, valid_subscription_stages: [...form.valid_subscription_stages, s] });
    setNewStage("");
  };

  const removeStage = (i) => {
    const arr = [...form.valid_subscription_stages];
    arr.splice(i, 1);
    setForm({ ...form, valid_subscription_stages: arr });
  };

  if (loading || !form) {
    return (
      <Layout>
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="admin-a1-settings-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
              style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>
                Agente A1 · Configuración
              </span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#0A3747" }}>Reglas del Agente A1</h1>
            <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
              Cambios aplican en tiempo real (caché TTL 30 s). Sin necesidad de redesplegar.
            </p>
            {settings.updated_at && (
              <p className="text-[11px] text-gray-500 mt-1">
                Último cambio: {new Date(settings.updated_at).toLocaleString("es-EC")} · por <b>{settings.updated_by}</b>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {dirty && (
              <button onClick={load} className="neu-btn" data-testid="a1-settings-revert">
                <RotateCcw size={14} /> Revertir
              </button>
            )}
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="neu-btn neu-btn-primary"
              data-testid="a1-settings-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar cambios
            </button>
          </div>
        </div>

        {/* Suscripción · whitelist de estados */}
        <div className="neu-card p-5 space-y-4" data-testid="a1-settings-stages">
          <div className="flex items-center gap-2">
            <Users size={18} style={{ color: "#0A3747" }} />
            <h2 className="text-lg font-bold" style={{ color: "#0A3747" }}>
              Estados de suscripción válidos
            </h2>
          </div>
          <p className="text-xs text-gray-600">
            Whitelist de estados que aprueban la validación A1 #3. La comparación es case-insensitive y sin tildes.
          </p>
          <div className="flex flex-wrap gap-2">
            {form.valid_subscription_stages.map((s, i) => (
              <div
                key={`${s}-${i}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-sm font-medium"
              >
                <span>{s}</span>
                <button
                  onClick={() => removeStage(i)}
                  className="hover:bg-emerald-100 rounded-full p-0.5"
                  aria-label={`Quitar ${s}`}
                  data-testid={`a1-settings-remove-stage-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {form.valid_subscription_stages.length === 0 && (
              <span className="text-sm text-red-600">⚠️ Al menos uno es obligatorio</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newStage}
              onChange={(e) => setNewStage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addStage()}
              placeholder="Ej: En instalación nueva"
              className="neu-input flex-1"
              data-testid="a1-settings-new-stage-input"
            />
            <button onClick={addStage} className="neu-btn" data-testid="a1-settings-add-stage">
              <Plus size={14} /> Agregar
            </button>
          </div>
        </div>

        {/* Stock thresholds */}
        <div className="neu-card p-5 space-y-4" data-testid="a1-settings-stock">
          <div className="flex items-center gap-2">
            <Package size={18} style={{ color: "#0A3747" }} />
            <h2 className="text-lg font-bold" style={{ color: "#0A3747" }}>Alertas de stock del técnico</h2>
          </div>
          <p className="text-xs text-gray-600">
            Porcentaje del material del técnico consumido por las OTs del día. Si excede el umbral naranja se marca en advertencia; si excede el rojo se bloquea el auto-agendamiento.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <NumField
              label="Umbral naranja (advertencia)"
              testid="a1-settings-orange"
              value={Math.round(form.stock_orange_threshold * 100)}
              onChange={(v) => setForm({ ...form, stock_orange_threshold: Math.max(0, Math.min(100, v)) / 100 })}
              min={0} max={100} suffix="%"
              tone="orange"
            />
            <NumField
              label="Umbral rojo (bloqueo)"
              testid="a1-settings-red"
              value={Math.round(form.stock_red_threshold * 100)}
              onChange={(v) => setForm({ ...form, stock_red_threshold: Math.max(0, Math.min(100, v)) / 100 })}
              min={0} max={100} suffix="%"
              tone="red"
            />
          </div>
          {form.stock_orange_threshold >= form.stock_red_threshold && (
            <div className="text-xs text-red-600 flex items-center gap-1.5">
              <AlertTriangle size={12} /> El umbral naranja debe ser menor que el rojo
            </div>
          )}
        </div>

        {/* Timers */}
        <div className="neu-card p-5 space-y-4" data-testid="a1-settings-timers">
          <div className="flex items-center gap-2">
            <Clock size={18} style={{ color: "#0A3747" }} />
            <h2 className="text-lg font-bold" style={{ color: "#0A3747" }}>Tiempos de escalamiento</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <NumField
              label="Espera del cliente (WhatsApp)"
              testid="a1-settings-wait"
              value={form.wait_client_hours}
              onChange={(v) => setForm({ ...form, wait_client_hours: v })}
              min={1} max={720} suffix="horas"
              hint="Tras enviar WhatsApp por zona llena, se espera N horas antes de pasar la OT al agendador."
            />
            <NumField
              label="Escalar a supervisor"
              testid="a1-settings-escalate"
              value={form.escalate_supervisor_hours}
              onChange={(v) => setForm({ ...form, escalate_supervisor_hours: v })}
              min={1} max={720} suffix="horas"
              hint="Tras N horas sin resolución en cualquier estado pending_*, escala al supervisor."
            />
          </div>
        </div>

        {/* Zona · override */}
        <div className="neu-card p-5 space-y-4" data-testid="a1-settings-zone">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} style={{ color: "#0A3747" }} />
            <h2 className="text-lg font-bold" style={{ color: "#0A3747" }}>Capacidad de zona (override global)</h2>
          </div>
          <p className="text-xs text-gray-600">
            Si es <b>0</b>, se respeta la capacidad diaria configurada en cada zona (<code>/admin/zonas</code>).
            Si es <b>&gt; 0</b>, este valor se aplica a TODAS las zonas activas y sobreescribe el valor individual.
            Útil para "modo mantenimiento" o eventos masivos que reducen capacidad general.
          </p>
          <NumField
            label="Capacidad diaria por zona (0 = usar la de cada zona)"
            testid="a1-settings-zone-override"
            value={form.zone_capacity_override}
            onChange={(v) => setForm({ ...form, zone_capacity_override: v })}
            min={0} max={9999} suffix="OTs/día"
          />
        </div>

        {/* Note explicativa */}
        <div className="text-[11px] text-gray-500 text-center">
          Los cambios se guardan en <code>a1_settings</code> (tabla singleton) y se aplican via caché in-memory con TTL 30 s. Sin redeploy.
        </div>
      </div>
    </Layout>
  );
}

function NumField({ label, value, onChange, min, max, suffix, testid, hint, tone }) {
  const borderTone =
    tone === "orange" ? "border-orange-400" :
    tone === "red" ? "border-red-400" : "border-gray-300";
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1">{label}</div>
      <div className={`flex items-center rounded-lg bg-white border ${borderTone} focus-within:ring-2 focus-within:ring-blue-300`}>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 px-3 py-2 bg-transparent focus:outline-none tabular-nums text-lg font-semibold"
          data-testid={testid}
        />
        {suffix && <span className="px-3 text-xs text-gray-500 border-l border-gray-200">{suffix}</span>}
      </div>
      {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
    </label>
  );
}
